/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { IComponent, IComponentProps, IImageData, msdyn365Commerce } from '@msdyn365-commerce/core';
import { ICartState } from '@msdyn365-commerce/global-state';
import { format, getPayloadObject, getTelemetryAttributes, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { FaShoppingBag } from 'react-icons/fa';

/**
 * ICartIconData: Interface for Cart Icon Data.
 */
export interface ICartIconData {
    cart?: ICartState;
}

/**
 * ICartIconComponentProps: Interface for Cart Icon component props.
 */
export interface ICartIconComponentProps extends IComponentProps<ICartIconData> {
    className?: string;
    cartLabel: string;
    cartQtyLabel: string;
    telemetryContent?: ITelemetryContent;
    cartIconImage?: IImageData;
}

/**
 * ICartIconComponent: Interface for Cart Icon component.
 */
export interface ICartIconComponent extends IComponent<ICartIconComponentProps> {}

const CartIconComponentActions = {};

/**
 *
 * CartIcon component.
 * @extends {React.FC<ICartIconComponentProps>}
 */
const CartIcon: React.FC<ICartIconComponentProps> = observer((props: ICartIconComponentProps) => {
    // console.log(props);
    const {
        cartLabel,
        cartQtyLabel,
        cartIconImage,
        data: { cart }
    } = props;
    const defaultCartItemCount: number = 0;

    console.log('Cart Icon props', props);

    // If there are invoice lines, don't update mini cart item quantity
    let cartItem = defaultCartItemCount;

    if (cart) {
        cartItem = cart.hasInvoiceLine ? defaultCartItemCount : cart.totalItemsInCart;
    }

    let qtyLabel: string = '';
    // console.log(format);
    const cartMaxQuantity: number = 99;
    const maxQuantityLabel: string = '99+';
    if (cartItem > cartMaxQuantity) {
        qtyLabel = maxQuantityLabel;
    } else {
        qtyLabel = format(cartQtyLabel, cartItem);
    }
    const label = format(cartLabel, cartItem);

    // Construct telemetry attribute to render
    const payLoad = getPayloadObject('click', props.telemetryContent!, 'cart-icon', '');
    // console.log(payLoad);
    const attributes = getTelemetryAttributes(props.telemetryContent!, payLoad);
    // console.log(attributes);
    const style: React.CSSProperties = {
        visibility: cart ? 'visible' : 'hidden'
    };

    console.log("cart icon image", props.cartIconImage);
    return (
        <div className='msc-cart-icon' role='button' aria-label={label} title={label} {...attributes}>
            {cartIconImage?.src ? <img src={cartIconImage?.src} alt='Kete icon' aria-label='kete icon' /> : <FaShoppingBag />}

            <div className='msc-cart-icon__count' aria-hidden style={style}>
                {qtyLabel}
            </div>
        </div>
    );
});

// @ts-expect-error -- Compatible issue with the component override.
export const CartIconComponent: React.FunctionComponent<ICartIconComponentProps> = msdyn365Commerce.createComponentOverride<ICartIconComponent
>('CartIcon', { component: CartIcon, ...CartIconComponentActions });

export default CartIconComponent;
